// src/components/aols/profile/AH.tsx
import React from "react";
import { Layout, Typography, Table, Button, Collapse } from "antd";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import styled from "styled-components";
import logo from "/logo.jpg";
import { Footer } from "../../dev/utils/Footer";
import { FacebookOutlined, LinkedinOutlined, YoutubeOutlined } from "@ant-design/icons";
import * as HoverCardPrimitives from "@radix-ui/react-hover-card";
import clsx from "clsx";
import "../../../tracker-tailwind.css";

const { Title, Paragraph } = Typography;
const { Panel } = Collapse;

const StyledLayout = styled(Layout)`
  min-height: 100vh;
  background: linear-gradient(180deg, #000000 0%, #1a1a1a 100%);
`;

const Header = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  background: #1a1a1a;
  color: white;
  height: 64px;
  padding: 10px 0;
  position: fixed;
  top: 0;
  width: 100%;
  z-index: 100;

  @media (max-width: 768px) {
    flex-direction: column;
    height: auto;
    padding: 10px;
  }
`;

const CustomButton = styled(Button)`
  margin-right: 20px;
  background: #3552fc;
  color: white;
  border: none;
  padding: 8px 20px;
  border-radius: 5px;
  font-size: 18px;
  transition: background 0.3s ease;

  &:hover {
    background: #40a9ff;
    color: white;
  }

  @media (max-width: 768px) {
    padding: 6px 15px;
    font-size: 16px;
  }
`;

const MainContent = styled.section`
  padding: 100px 20px 20px;
  text-align: center;
  flex: 1;
  min-height: calc(100vh - 134px);
  background: #1a1a1a;

  @media (max-width: 768px) {
    padding: 120px 10px 10px;
  }
`;

const ProfileContainer = styled.div`
  background: #1a1a1a;
  color: #0427f4;
  margin: 0 auto;
  max-width: 900px;
  padding: 40px;
  border-radius: 8px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
  align-items: center;

  @media (max-width: 768px) {
    padding: 20px;
    margin: 10px;
  }
`;

const CustomBreadcrumb = styled.div`
  margin-bottom: 20px;
  text-align: left;
  font-size: 14px;
  line-height: 1.5714285714285714;

  .breadcrumb-item {
    display: inline-block;
  }

  .breadcrumb-link {
    color: #ffaa00 !important;
    text-decoration: none;
  }

  .breadcrumb-link:hover {
    color: #fce248 !important;
  }

  .breadcrumb-separator {
    color: #fff;
    margin: 0 8px;
  }

  .breadcrumb-non-active {
    color: #fff !important;
  }
`;

const FeatureCard = styled.div`
  background: #fce248;
  padding: 20px;
  margin: 15px 0;
  border-radius: 8px;
  border: 2px solid #ffcc00;
  text-align: left;
  color: #000 !important;

  @media (max-width: 768px) {
    padding: 15px;
  }
`;

const TrackerFeatureCard = styled.div`
  background: #fff;
  padding: 30px;
  margin: 15px 0;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  text-align: left;
  color: #000;
  min-height: 300px;

  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const TrackerContainer = styled.div`
  width: 100%;
  max-width: 100%;
  margin: 20px 0;
  padding: 20px;
`;

const TrackerRow = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 15px;
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const TrackerYear = styled.span`
  width: 100px;
  color Wrote: : #000;
  font-size: 20px;
  font-weight: 500;
  margin-right: 10px;
  @media (max-width: 768px) {
    width: 100%;
    margin-bottom: 5px;
    font-size: 18px;
  }
`;

const CallToActionButton = styled(Button)`
  margin-top: 30px;
  background: linear-gradient(45deg, #ffcc00, #ffaa00, #ff8800);
  color: white;
  border: none;
  font-size: 20px;
  padding: 15px 40px;
  border-radius: 25px;
  box-shadow: 0 4px 15px rgba(255, 204, 0, 0.5);
  font-weight: bold;
  transition: background 0.3s ease, box-shadow 0.3s ease, transform 0.3s ease;

  &:hover {
    background: linear-gradient(45deg, #ffaa00, #ff8800, #ff6600);
    box-shadow: 0 6px 20px rgba(255, 204, 0, 0.7);
    transform: translateY(-2px);
    color: white;
  }

  @media (max-width: 768px) {
    font-size: 16px;
    padding: 10px 30px;
  }
`;

const StyledFooter = styled(Layout.Footer)`
  background: #ffcc00;
  color: #333;
  text-align: center;
  padding: 15px 0;
  font-size: 14px;
  position: relative;
  bottom: 0;
  width: 100%;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);

  a {
    color: #333;
    text-decoration: none;
  }

  a:hover {
    text-decoration: underline;
  }
`;

interface TrackerBlockProps {
  key?: string | number;
  color?: string;
  tooltip?: string;
  hoverEffect?: boolean;
  defaultBackgroundColor?: string;
}

const Block = ({
  color,
  tooltip,
  defaultBackgroundColor,
  hoverEffect,
}: TrackerBlockProps) => {
  const [open, setOpen] = React.useState(false);
  return (
    <HoverCardPrimitives.Root
      open={open}
      onOpenChange={setOpen}
      openDelay={0}
      closeDelay={0}
      tremor-id="tremor-raw"
    >
      <HoverCardPrimitives.Trigger onClick={() => setOpen(true)} asChild>
        <div className="size-full overflow-hidden px-[0.5px] transition first:rounded-l-[4px] first:pl-0 last:rounded-r-[4px] last:pr-0 sm:px-px">
          <div
            className={clsx(
              "size-full rounded-[1px] tracker-block",
              color || defaultBackgroundColor,
              hoverEffect ? "hover:opacity-50" : "",
            )}
          />
        </div>
      </HoverCardPrimitives.Trigger>
      <HoverCardPrimitives.Portal>
        <HoverCardPrimitives.Content
          sideOffset={10}
          side="top"
          align="center"
          avoidCollisions
          className={clsx(
            "w-auto rounded-md px-2 py-1 text-sm shadow-md text-white bg-gray-900",
          )}
        >
          {tooltip}
        </HoverCardPrimitives.Content>
      </HoverCardPrimitives.Portal>
    </HoverCardPrimitives.Root>
  );
};

Block.displayName = "Block";

interface TrackerProps extends React.HTMLAttributes<HTMLDivElement> {
  data: TrackerBlockProps[];
  defaultBackgroundColor?: string;
  hoverEffect?: boolean;
}

const Tracker = React.forwardRef<HTMLDivElement, TrackerProps>(
  (
    {
      data = [],
      defaultBackgroundColor = "bg-gray-400",
      className,
      hoverEffect,
      ...props
    },
    forwardedRef,
  ) => {
    return (
      <div
        ref={forwardedRef}
        className={clsx("flex h-12 w-full items-center", className)} // Increased height from h-8 to h-12
        {...props}
      >
        {data.map((props, index) => (
          <Block
            key={props.key ?? index}
            defaultBackgroundColor={defaultBackgroundColor}
            hoverEffect={hoverEffect}
            {...props}
          />
        ))}
      </div>
    );
  },
);

Tracker.displayName = "Tracker";

const AHPage: React.FC = () => {
  const socialLinks = [
    {
      url: "https://www.facebook.com",
      icon: <FacebookOutlined />,
    },
    {
      url: "https://www.linkedin.com",
      icon: <LinkedinOutlined />,
    },
    {
      url: "https://www.youtube.com",
      icon: <YoutubeOutlined />,
    },
  ];

  const teacherInfo = {
    name: "A. H.",
    title: "OLS Student",
    lightMinutes: "348",
    lightHours: "5.8",
    lightDays: "0.24",
  };

  const profileData = [
    { key: "1", year: "3405/06", months: { "5": "3.1", "6": "", "7": "", "8": "", "9": "", "10": "", "11": "", "12": "", "1": "", "2": "", "3": "", "4": "" } },
    { key: "2", year: "3404/05", months: { "5": "33", "6": "13.4", "7": "10.8", "8": "9", "9": "35", "10": "30", "11": "10", "12": "50.8", "1": "17", "2": "", "3": "", "4": "8.8" } },
    { key: "3", year: "3403/04", months: { "5": "", "6": "", "7": "", "8": "", "9": "", "10": "", "11": "", "12": "28", "1": "12", "2": "27.5", "3": "29.4", "4": "31" } },
  ];

  const monthOrder = ["5", "6", "7", "8", "9", "10", "11", "12", "1", "2", "3", "4"];
  const monthNames = ["May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec", "Jan", "Feb", "Mar", "Apr"];

  const totals = profileData.reduce((acc, row) => {
    monthOrder.forEach((month) => {
      const value = parseFloat(row.months[month]) || 0;
      acc[month] = (parseFloat(acc[month] || 0) + value).toFixed(1);
    });
    return acc;
  }, {});

  const getColorForValue = (value: string) => {
    const val = parseFloat(value) || 0;
    if (val <= 0) return "bg-gray-200";
    if (val <= 49) return "bg-yellow-200";
    if (val <= 150) return "bg-yellow-300";
    if (val <= 300) return "bg-yellow-500";
    if (val <= 1000) return "bg-amber-700";
    if (val <= 2000) return "bg-red-400";
    return "bg-red-600";
  };

  const trackerData = profileData.map((row) => {
    const trackerRow = monthOrder.map((month, index) => {
      const value = row.months[month] || "0";
      const color = getColorForValue(value);
      const tooltip = `${monthNames[index]} ${row.year}: ${value} Light Minutes`;
      return { color, tooltip };
    });
    return { year: row.year, data: trackerRow };
  });

  const columns = [
    { title: "Year", dataIndex: "year", key: "year", width: 100 },
    ...monthOrder.map((month, index) => ({
      title: monthNames[index],
      dataIndex: ["months", month],
      key: month,
    })),
  ];

  return (
    <StyledLayout>
      <Helmet>
        <title>A. H. - OLS Student | onelightsystem OLS</title>
        <meta
          name="description"
          content="Discover A. H., an OLS Student with 343 Light Minutes, contributing to Sun Light Meditation education at onelightsystem OLS."
        />
        <meta
          name="keywords"
          content="A. H., OLS Student, Sun Light Meditation, onelightsystem OLS, Meditation Education"
        />
        <meta name="author" content="onelightsystem OLS" />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="A. H. - OLS Student | onelightsystem OLS" />
        <meta
          property="og:description"
          content="Discover A. H., an OLS Student with 343 Light Minutes, contributing to Sun Light Meditation education at onelightsystem OLS."
        />
        <meta property="og:url" content="https://www.olsme.com/ah" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://media.licdn.com/dms/image/v2/D5603AQFO2oVdSlBbHw/profile-displayphoto-shrink_100_100/profile-displayphoto-shrink_100_100/0/1692414820173?e=1754524800&v=beta&t=RYQWjxzJr3F-Z9Nvl3Lx9aC-83PXG7QcrwuPBCK8B40" />
        <meta property="og:image:alt" content="A. H., OLS Student" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="A. H. - OLS Student | onelightsystem OLS" />
        <meta
          name="twitter:description"
          content="Discover A. H., an OLS Student with 343 Light Minutes, contributing to Sun Light Meditation education at onelightsystem OLS."
        />
        <meta name="twitter:image" content="https://media.licdn.com/dms/image/v2/D5603AQFO2oVdSlBbHw/profile-displayphoto-shrink_100_100/profile-displayphoto-shrink_100_100/0/1692414820173?e=1754524800&v=beta&t=RYQWjxzJr3F-Z9Nvl3Lx9aC-83PXG7QcrwuPBCK8B40" />
        <link rel="canonical" href="https://www.olsme.com/ah" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "Stat",
                item: "https://www.olsme.com/Stat",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: "A. H.",
                item: "https://www.olsme.com/ah",
              },
            ],
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Person",
            name: "A. H.",
            jobTitle: "OLS Student",
            description: "A. H. is an OLS Student contributing to Sun Light Meditation education at onelightsystem OLS with 343 Light Minutes.",
            url: "https://www.olsme.com/ah",
            image: "https://media.licdn.com/dms/image/v2/D5603AQFO2oVdSlBbHw/profile-displayphoto-shrink_100_100/profile-displayphoto-shrink_100_100/0/1692414820173?e=1754524800&v=beta&t=RYQWjxzJr3F-Z9Nvl3Lx9aC-83PXG7QcrwuPBCK8B40",
            affiliation: {
              "@type": "Organization",
              name: "onelightsystem OLS",
              url: "https://www.olsme.com",
            },
            knowsAbout: ["Sun Light Meditation", "Meditation Education"],
            sameAs: [
              "https://www.facebook.com",
              "https://www.linkedin.com",
              "https://www.youtube.com",
            ],
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "onelightsystem OLS",
            url: "https://www.olsme.com",
            logo: "https://www.olsme.com/logo.jpg",
            description: "A global platform advancing Sun Light Meditation and fostering a Light Civilization.",
            founder: {
              "@type": "Person",
              name: "Nazar Pankiv",
              alternateName: "Asvitloaten",
            },
          })}
        </script>
      </Helmet>

      <Header>
        <Link to="/">
          <img src={logo} alt="OneLightSystem OLS Logo" style={{ height: "50px", marginRight: "20px" }} />
        </Link>
        <CustomButton>
          <Link to="/Stat">OLS Statistic</Link>
        </CustomButton>
      </Header>

      <MainContent>
        <ProfileContainer>
          <CustomBreadcrumb>
            <span className="breadcrumb-item">
              <Link to="/Stat" className="breadcrumb-link">
                Stat
              </Link>
            </span>
            <span className="breadcrumb-separator">/</span>
            <span className="breadcrumb-item">
              <Link to="/ah" className="breadcrumb-link">
                A. H.
              </Link>
            </span>
          </CustomBreadcrumb>

          <Title level={1} style={{ color: "#fff" }}>
            onelightsystem OLS Student A. H.
          </Title>

          <FeatureCard>
            <div style={{ display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "center", gap: "20px", width: "100%" }}>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                <img
                  src="https://media.licdn.com/dms/image/v2/D5603AQFO2oVdSlBbHw/profile-displayphoto-shrink_100_100/profile-displayphoto-shrink_100_100/0/1692414820173?e=1754524800&v=beta&t=RYQWjxzJr3F-Z9Nvl3Lx9aC-83PXG7QcrwuPBCK8B40"
                  alt="A. H. Profile Picture"
                  onError={(e) => (e.currentTarget.src = "https://via.placeholder.com/200")}
                  style={{ width: "150px", height: "150px", borderRadius: "50%", marginBottom: "10px" }}
                />
                <div style={{ display: "flex", gap: "15px", margin: "10px 0", justifyContent: "center" }}>
                  {socialLinks.map((link, index) => (
                    <a
                      key={index}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ fontSize: "24px", color: "#000", transition: "color 0.3s ease" }}
                      onMouseEnter={(e) => (e.currentTarget.style.color = "#ffcc00")}
                      onMouseLeave={(e) => (e.currentTarget.style.color = "#000")}
                    >
                      {link.icon}
                    </a>
                  ))}
                </div>
              </div>
              <div style={{ textAlign: "left", padding: "10px", background: "rgba(0, 0, 0, 0.7)", borderRadius: "5px", flex: 1 }}>
                <Title level={2} style={{ color: "#fff", fontSize: "3em", margin: 0 }}>{teacherInfo.name}</Title>
                <Paragraph style={{ color: "#fff", fontSize: "1.5em", margin: "5px 0" }}>{teacherInfo.title}</Paragraph>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "15px", margin: "10px 0" }}>
                  <div style={{ background: "#ffaa00", padding: "15px 20px", borderRadius: "5px", textAlign: "center", flex: 1, minWidth: "120px" }}>
                    <span style={{ display: "block", fontSize: "1.2em", color: "#0427f4", fontWeight: "bold" }}>Light Minutes</span>
                    <span style={{ display: "block", fontSize: "1.8em", color: "#000", fontWeight: "bold" }}>{teacherInfo.lightMinutes}</span>
                  </div>
                  <div style={{ background: "#ffaa00", padding: "15px 20px", borderRadius: "5px", textAlign: "center", flex: 1, minWidth: "120px" }}>
                    <span style={{ display: "block", fontSize: "1.2em", color: "#0427f4", fontWeight: "bold" }}>Light Hours</span>
                    <span style={{ display: "block", fontSize: "1.8em", color: "#000", fontWeight: "bold" }}>{teacherInfo.lightHours}</span>
                  </div>
                  <div style={{ background: "#ffaa00", padding: "15px 20px", borderRadius: "5px", textAlign: "center", flex: 1, minWidth: "120px" }}>
                    <span style={{ display: "block", fontSize: "1.2em", color: "#0427f4", fontWeight: "bold" }}>Light Days</span>
                    <span style={{ display: "block", fontSize: "1.8em", color: "#000", fontWeight: "bold" }}>{teacherInfo.lightDays}</span>
                  </div>
                </div>
              </div>
            </div>
          </FeatureCard>

          {/* TrackerFeatureCard Section - Modify this section for OLSLM Activity styling and data */}
          <TrackerFeatureCard>
            <TrackerContainer>
              <Title level={3} style={{ color: "#000", marginBottom: "10px" }}>OLSLM Activity</Title>
              <Paragraph style={{ color: "#666", fontStyle: "italic", marginBottom: "20px" }}>
                (onelightsystem OLS Light Minutes Active Meditation)
              </Paragraph>
              {trackerData.map((tracker, index) => (
                <TrackerRow key={index}>
                  <TrackerYear>{tracker.year}</TrackerYear>
                  <Tracker data={tracker.data} hoverEffect={true} />
                </TrackerRow>
              ))}
              <div style={{ display: "flex", gap: "10px", marginTop: "20px" }}>
                <CustomButton>
                  <Link to="/register/olssregister" target="_blank">Become OLS Student</Link>
                </CustomButton>
                <CustomButton>
                  <Link to="/privacy" target="_blank">OLS Privacy</Link>
                </CustomButton>
              </div>
            </TrackerContainer>
          </TrackerFeatureCard>

          <Collapse style={{ margin: "15px 0" }}>
            <Panel header="onelightsystem OLSLM Data" key="1" style={{ background: "#fce248", color: "#000", fontSize: "16px", fontWeight: 500 }}>
              <FeatureCard>
                <div style={{ width: "100%", overflowX: "auto", margin: "20px 0" }}>
                  <Table
                    columns={columns}
                    dataSource={profileData}
                    pagination={false}
                    size="small"
                    scroll={{ x: "max-content" }}
                    summary={() => (
                      <Table.Summary.Row style={{ background: "#ffaa00", color: "#000", fontWeight: "bold" }}>
                        <Table.Summary.Cell index={0}>Total</Table.Summary.Cell>
                        {monthOrder.map((month, index) => (
                          <Table.Summary.Cell key={month} index={index + 1}>{totals[month]}</Table.Summary.Cell>
                        ))}
                      </Table.Summary.Row>
                    )}
                    rowClassName={() => "no-hover"}
                    components={{
                      header: {
                        cell: (props) => <th {...props} style={{ ...props.style, background: "#fff", color: "#000", border: "1px solid #999" }} />,
                      },
                      body: {
                        cell: (props) => <td {...props} style={{ ...props.style, background: "#fce248", color: "#000", border: "1px solid #999" }} />,
                      },
                      summary: {
                        cell: (props) => <td {...props} style={{ ...props.style, background: "#ffaa00", color: "#000", border: "1px solid #999" }} />,
                      },
                    }}
                  />
                </div>
              </FeatureCard>
            </Panel>
          </Collapse>

          <CallToActionButton>
            <Link to="/register/olssregister" target="_blank">Apply to Become an OLS Student</Link>
          </CallToActionButton>
        </ProfileContainer>
      </MainContent>
      <StyledFooter>
        <Footer />
      </StyledFooter>
    </StyledLayout>
  );
};

export default AHPage;